import React from "react"
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Box,
	IconButton,
	Typography,
	CircularProgress,
} from "@material-ui/core"
import AddIcon from "@material-ui/icons/Add"
import DeleteIcon from "@material-ui/icons/Delete"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

type CreateOfferModalProps = {
	customerID: string
	open: boolean
	onClose: () => void
	onSuccess: () => void
}

type TierFormData = {
	tierId: string
	tierType: string
	minPayments: string
	maxPayments: string
	discountRate: string
	minPayment: string
	currency: string
	timeFrameWeeks: string
}

function CreateOfferModal({customerID, open, onClose, onSuccess}: CreateOfferModalProps) {
	const {notification} = useNotification()
	const [loading, setLoading] = React.useState(false)
	const [errors, setErrors] = React.useState<Record<string, string>>({})
	const [offerID] = React.useState(crypto.randomUUID())
	const [startDate, setStartDate] = React.useState("")
	const [endDate, setEndDate] = React.useState("")
	const [campaignID, setCampaignID] = React.useState("")
	const [agentEmail, setAgentEmail] = React.useState("")
	const [notes, setNotes] = React.useState("")
	const [tiers, setTiers] = React.useState<TierFormData[]>([
		{
			tierId: crypto.randomUUID(),
			tierType: "ONE_OFF",
			minPayments: "",
			maxPayments: "1",
			discountRate: "0.20",
			minPayment: "100",
			currency: "AUD",
			timeFrameWeeks: "4",
		},
	])

	const handleAddTier = () => {
		setTiers([
			...tiers,
			{
				tierId: crypto.randomUUID(),
				tierType: "PLAN",
				minPayments: "",
				maxPayments: "",
				discountRate: "",
				minPayment: "",
				currency: "AUD",
				timeFrameWeeks: "",
			},
		])
	}

	const handleRemoveTier = (index: number) => {
		setTiers(tiers.filter((_, i) => i !== index))
	}

	const handleTierChange = (index: number, field: keyof TierFormData, value: string) => {
		const newTiers = [...tiers]
		newTiers[index][field] = value
		setTiers(newTiers)
	}

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {}

		if (!startDate) {
			newErrors.startDate = "Start date is required"
		}

		if (!endDate) {
			newErrors.endDate = "End date is required"
		}

		if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
			newErrors.endDate = "End date must be after start date"
		}

		if (tiers.length === 0) {
			newErrors.tiers = "At least one tier is required"
		}

		tiers.forEach((tier, index) => {
			if (!tier.maxPayments || parseInt(tier.maxPayments) <= 0) {
				newErrors[`tier${index}maxPayments`] = "Max payments is required and must be greater than 0"
			}

			if (!tier.discountRate || parseFloat(tier.discountRate) < 0 || parseFloat(tier.discountRate) >= 1) {
				newErrors[`tier${index}discountRate`] = "Discount rate must be between 0 and 1"
			}

			if (!tier.minPayment || parseInt(tier.minPayment) <= 0) {
				newErrors[`tier${index}minPayment`] = "Min payment is required and must be greater than 0"
			}

			if (!tier.timeFrameWeeks || parseInt(tier.timeFrameWeeks) <= 0) {
				newErrors[`tier${index}timeFrameWeeks`] = "Time frame is required and must be greater than 0"
			}

			if (tier.minPayments && parseInt(tier.minPayments) < 0) {
				newErrors[`tier${index}minPayments`] = "Min payments must be 0 or greater"
			}

			if (tier.minPayments && tier.maxPayments && parseInt(tier.minPayments) > parseInt(tier.maxPayments)) {
				newErrors[`tier${index}minPayments`] = "Min payments must be less than or equal to max payments"
			}
		})

		if (agentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(agentEmail)) {
			newErrors.agentEmail = "Invalid email format"
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = async () => {
		if (!validateForm()) {
			return
		}

		setLoading(true)
		try {
			await api.customer.createOffer(customerID, {
				offerId: offerID,
				startDate,
				endDate,
				tiers: tiers.map((tier) => {
					const tierData = {
						tierId: tier.tierId,
						tierType: tier.tierType,
						maxPayments: parseInt(tier.maxPayments),
						discountRate: parseFloat(tier.discountRate),
						minPayment: parseInt(tier.minPayment),
						currency: tier.currency,
						timeFrameWeeks: parseInt(tier.timeFrameWeeks),
						...(tier.minPayments && {minPayments: parseInt(tier.minPayments)}),
					}
					return tierData
				}),
				campaignId: campaignID || undefined,
				agentEmail: agentEmail || undefined,
				notes: notes || undefined,
			})

			notification.success("Customer offer created successfully")
			onSuccess()
		} catch (error: any) {
			notification.error(error.Message || "Failed to create customer offer")
		} finally {
			setLoading(false)
		}
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Create Customer Offer</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" style={{gap: "16px"}}>
					<TextField
						label="Start Date"
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{shrink: true}}
						error={!!errors.startDate}
						helperText={errors.startDate}
						fullWidth
						required
					/>
					<TextField
						label="End Date"
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{shrink: true}}
						error={!!errors.endDate}
						helperText={errors.endDate}
						fullWidth
						required
					/>
					<TextField
						label="Campaign ID (optional)"
						value={campaignID}
						onChange={(e) => setCampaignID(e.target.value)}
						fullWidth
					/>
					<TextField
						label="Agent Email (optional)"
						value={agentEmail}
						onChange={(e) => setAgentEmail(e.target.value)}
						error={!!errors.agentEmail}
						helperText={errors.agentEmail}
						fullWidth
					/>
					<TextField
						label="Notes (optional)"
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						multiline
						rows={3}
						fullWidth
					/>

					<Box>
						<Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
							<Typography variant="h6">Tiers</Typography>
							<Button startIcon={<AddIcon />} onClick={handleAddTier} color="primary">
								Add Tier
							</Button>
						</Box>

						{tiers.map((tier, index) => (
							<Box key={index} mb={2} p={2} border={1} borderColor="grey.300" borderRadius={4}>
								<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
									<Typography variant="subtitle1">Tier {index + 1}</Typography>
									{tiers.length > 1 && (
										<IconButton size="small" onClick={() => handleRemoveTier(index)}>
											<DeleteIcon />
										</IconButton>
									)}
								</Box>

								<Box display="grid" gridTemplateColumns="1fr 1fr" style={{gap: "16px"}}>
									<TextField
										label="Tier Type"
										select
										SelectProps={{native: true}}
										value={tier.tierType}
										onChange={(e) => handleTierChange(index, "tierType", e.target.value)}
										fullWidth
										required
									>
										<option value="ONE_OFF">One-Off</option>
										<option value="PLAN">Plan</option>
									</TextField>

									<TextField
										label="Min Payments"
										type="number"
										value={tier.minPayments}
										onChange={(e) => handleTierChange(index, "minPayments", e.target.value)}
										error={!!errors[`tier${index}minPayments`]}
										helperText={errors[`tier${index}minPayments`]}
										fullWidth
									/>

									<TextField
										label="Max Payments"
										type="number"
										value={tier.maxPayments}
										onChange={(e) => handleTierChange(index, "maxPayments", e.target.value)}
										error={!!errors[`tier${index}maxPayments`]}
										helperText={errors[`tier${index}maxPayments`]}
										fullWidth
										required
									/>

									<TextField
										label="Discount Rate"
										type="number"
										inputProps={{step: "0.01", min: "0", max: "1"}}
										value={tier.discountRate}
										onChange={(e) => handleTierChange(index, "discountRate", e.target.value)}
										error={!!errors[`tier${index}discountRate`]}
										helperText={errors[`tier${index}discountRate`]}
										fullWidth
										required
									/>

									<TextField
										label="Min Payment (cents)"
										type="number"
										value={tier.minPayment}
										onChange={(e) => handleTierChange(index, "minPayment", e.target.value)}
										error={!!errors[`tier${index}minPayment`]}
										helperText={errors[`tier${index}minPayment`]}
										fullWidth
										required
									/>

									<TextField
										label="Currency"
										select
										SelectProps={{native: true}}
										value={tier.currency}
										onChange={(e) => handleTierChange(index, "currency", e.target.value)}
										fullWidth
										required
									>
										<option value="AUD">AUD</option>
										<option value="USD">USD</option>
										<option value="NZD">NZD</option>
									</TextField>

									<TextField
										label="Time Frame (weeks)"
										type="number"
										value={tier.timeFrameWeeks}
										onChange={(e) => handleTierChange(index, "timeFrameWeeks", e.target.value)}
										error={!!errors[`tier${index}timeFrameWeeks`]}
										helperText={errors[`tier${index}timeFrameWeeks`]}
										fullWidth
										required
									/>
								</Box>
							</Box>
						))}
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={loading}>
					Cancel
				</Button>
				<Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
					{loading ? <CircularProgress size={24} /> : "Create Offer"}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export {CreateOfferModal}
